import { Box, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../../utils/api';
import Alert from '../../../common/Alert';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import TextField from '../../../common/TextField';
import JobDescriptionPopup from './JobDescriptionPopup';
import { VacancyApiResponse } from './VacancyForm';
import { AsyncAutocomplete, Option } from '../../../common/AsyncAutocomplete';

const buttonStyle = {
  '&.MuiButton-contained': {
    backgroundColor: '#E95845',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#E95845',
    },
  }
}

interface VacancyJDForm {
  city: string;
  country: string;
  aboutClient: string;
  aboutEyepax: string;
  isGeneratedJD?: boolean;
  jobDescription: string;
  description: string;
}

interface VacancyJDFormProps {
  type: 'add' | 'edit' | 'view';
  onCancel: () => void;
  onSave: () => void;
}

const VacancyJDForm: React.FC<VacancyJDFormProps> = ({ type, onCancel, onSave }) => {
  const params = useParams();
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [aboutClient, setAboutClient] = useState('');
  const [aboutEyepax, setAboutEyepax] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState<Option | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [country, setCountry] = useState<Option | null>(null);
  const [isJDInProgress, setIsJDInProgress] = useState(false);
  const [openJobDescription, setOpenJobDescription] = useState(false);

  useEffect(() => {
    const vacancyId = params?.id;
    if (type === 'add' || vacancyId === undefined || vacancyId === null) return;

    setIsLoading(true);
    api.get<VacancyApiResponse>(
      `/rec/vacancy/${vacancyId}`,
      {
        headers: {
          'x-api-id': 11,
          'x-page-id': type === 'view' ? 13 : 10,
        },
      }
    ).then((response) => {
      const data = response.data;

      setCity(data.city ? { value: data.city.id.toString(), label: data.city.name } : null);
      setTitle(data.vacancyTitle);
      setCountry(data.country ? { value: data.country.id.toString(), label: data.country.name } : null);
      setAboutClient(data.vacancyAboutClient);
      setAboutEyepax(data.vacancyAboutEyepax);
      setJobDescription(data.jobDescription ? data.jobDescription : data.jobDescriptionAI);
      setDescription(data.description);
      setIsJDInProgress(data.jdInProgress || false);
      setIsLoading(false);
    });
  }, [type, params.id]);

  const handleSave = async (isGenerateJD: boolean) => {
    try {
      setIsSaving(true);
      setIsJDInProgress(isGenerateJD);
      const vacancyId = params.id;
      if (!vacancyId) return;

      const formData = new FormData();

      formData.append('city', city?.value || '');
      formData.append('country', country?.value || '');
      formData.append('vacancyAboutClient', aboutClient);
      formData.append('vacancyAboutEyepax', aboutEyepax);
      formData.append('jobDescription', jobDescription);
      formData.append('description', description);
      formData.append('isGenerateJD', isGenerateJD ? 'true' : 'false');

      const response = await api.put<VacancyApiResponse>(
        `/rec/vacancy/${vacancyId}/updateJD`,
        formData,
        {
          headers: {
            'x-api-id': 12,
            'x-page-id': 13,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        onSave?.();
      }
    } catch (error) {
      console.error('Error updating vacancy:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadCities = async (query: string) => {
    const response = await api.get<{ data: { id: number; city: string }[] }>(
      `/rec/vacancy/cities?query=${query}`,
      {
        headers: {
          'x-api-id': 6,
          'x-page-id': 7,
        },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadCountries = async (query: string) => {
    const response = await api.get<{ data: { id: number; country: string }[] }>(
      `/rec/vacancy/countries?query=${query}`,
      {
        headers: {
          'x-api-id': 6,
          'x-page-id': 7,
        },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? <Loading /> : (
        <>
          {isJDInProgress && (
            <Alert
              title="Info"
              message="Job Description Generation is in progress. Please wait for it to complete."
              width="1502px"
            />
          )}

          <Box className="edit-section">
            <Grid container spacing={4}>
              {/* First Row */}
              <Grid container size={12}>
                <Grid>
                  <AsyncAutocomplete
                    label="City"
                    width="360px"
                    value={city}
                    onChange={(option) => setCity(option)}
                    loadOptions={handleLoadCities}
                  />
                </Grid>
                <Grid>
                  <AsyncAutocomplete
                    label="Country"
                    width="360px"
                    value={country}
                    onChange={(option) => setCountry(option)}
                    loadOptions={handleLoadCountries}
                  />
                </Grid>
              </Grid>
              {/* Second Row */}
              <Grid container size={12}>
                <Grid size={12}>
                  <TextField
                    label="Vacancy Description"
                    multiline
                    rows={10}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder='Please include any additional skills, technologies, or qualifications in this section, if applicable.'
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box className="edit-section">
            <Grid container spacing={4}>
              {/* First Row */}
              <Grid container size={12}>
                <Grid size={12}>
                  <TextField
                    label="About Client"
                    multiline
                    rows={5}
                    value={aboutClient}
                    onChange={(e) => setAboutClient(e.target.value)}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                      },
                    }}
                  />
                </Grid>
              </Grid>
              {/* Second Row */}
              <Grid container size={12}>
                <Grid size={12}>
                  <TextField
                    label="About Eyepax"
                    multiline
                    rows={5}
                    value={aboutEyepax}
                    onChange={(e) => setAboutEyepax(e.target.value)}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '1502px' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {type !== 'view' && (
                <Button
                  variant="contained"
                  onClick={() => handleSave(true)}
                  width="231px"
                  sx={buttonStyle}
                  disabled={isSaving || isJDInProgress}
                >
                  Generate Job Description
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => setOpenJobDescription(true)}
                width="231px"
                disabled={isSaving || isJDInProgress}
              >
                View Job Description
              </Button>
            </Box>
            {type !== 'view' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  className="edit-section-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSave(false)}
                  className="edit-section-save-button"
                  disabled={isSaving || isJDInProgress}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
          </Box>

          <JobDescriptionPopup
            type={type}
            open={openJobDescription}
            initialJobDescription={jobDescription}
            onClose={() => setOpenJobDescription(false)}
            onSave={(jobDescription) => {
              setOpenJobDescription(false);
              setJobDescription(jobDescription);
            }}
            title={title}
            subtitle="Job Description"
          />
        </>
      )}
    </Box>
  );
};

export default VacancyJDForm; 